import { ColumnDef } from '@tanstack/react-table';
import { RiDeleteBinLine, RiPencilLine } from 'component-library';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DraftPlot } from '@/api/rest/resources/types/plot';
import { AreaCell, NameCell } from '@/pages/landsteward/pages/plot/pages/new/components/Cells';
import { ToggleSortButton } from '@/pages/landsteward/pages/plot/pages/new/components/ToggleSortButton';
import { CheckboxCell } from '@/pages/shared/projects/pages/project/pages/sustainableProduction/pages/plots/components/Cells';

export const useDraftPlotsTableColumns = (onDelete: (id: string) => void): ColumnDef<DraftPlot>[] => {
  const { t } = useTranslation();

  return useMemo<ColumnDef<DraftPlot>[]>(
    () => [
      {
        id: 'select',
        header: ({ table }) => (
          <CheckboxCell
            checked={table.getIsAllRowsSelected()}
            indeterminate={table.getIsSomeRowsSelected()}
            onChange={table.getToggleAllRowsSelectedHandler()}
          />
        ),
        cell: ({ row }) => (
          <CheckboxCell
            checked={row.getIsSelected()}
            disabled={!row.getCanSelect()}
            indeterminate={row.getIsSomeSelected()}
            onChange={row.getToggleSelectedHandler()}
          />
        ),
      },
      {
        accessorKey: 'name',
        header: ({ column }) => (
          <ToggleSortButton column={column} className='-ml-2.5'>
            {t('landSteward.projects.plots.tableHeader.name')}
          </ToggleSortButton>
        ),
        cell: (context) => (
          <div className='flex items-center justify-between gap-2'>
            <div className='min-w-0 flex-1'>
              <NameCell {...context} />
            </div>
            <RiPencilLine
              size={16}
              aria-hidden='true'
              className='shrink-0 text-text-secondary opacity-0 transition-opacity group-hover/row:opacity-100'
            />
          </div>
        ),
      },
      {
        accessorKey: 'area',
        header: ({ column }) => (
          <ToggleSortButton column={column} className='-ml-2.5'>
            {t('shared.uploadPlots.review.sizeColumn')}
          </ToggleSortButton>
        ),
        cell: AreaCell,
      },
      {
        id: 'delete',
        header: () => null,
        cell: ({ row }) => (
          <button
            type='button'
            aria-label={t('shared.uploadPlots.review.deleteAriaLabel')}
            onClick={(e) => {
              e.stopPropagation();
              onDelete(row.original.id);
            }}
            className='text-text-secondary opacity-0 transition-opacity hover:text-error-100 group-hover/row:opacity-100'
          >
            <RiDeleteBinLine size={16} />
          </button>
        ),
      },
    ],
    [t, onDelete],
  );
};
